<template>
  <div class="chart-wrapper">
    <!-- 로딩 스피너 -->
    <div
      v-if="isLoading"
      class="chart-loading"
      :class="{ 'chart-loading-dark': themeStore.isDark }"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">
        차트를 불러오는 중...
      </p>
    </div>

    <!-- 차트 영역 -->
    <div
      :id="chartid"
      ref="chartRef"
      class="highchart-container"
      :class="{ 'chart-hidden': isLoading }"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed} from "vue";
import type { Device, SensorData } from '@/types/commontypes'
import Highcharts from 'highcharts'
import moment from 'moment'
import { useThemeStore } from '../stores/themeStore'

const props = defineProps<{
  setsensor: SensorData;
  setdevice: Device;
}>();



const chartid = 'chart-' + Math.random().toString(36).slice(2,9);

const chartRef = ref<HTMLElement | null>(null)

const chartObj = ref<Highcharts.Chart | null>(null)

const isLoading = ref(true)

const themeStore = useThemeStore()

// 테마에 따른 색상 설정
const chartColors = computed(() => {
  return themeStore.isDark ? {
    backgroundColor: '#121e2d',
    textColor: '#e5e5ea',
    gridLineColor: '#535b5f',
    lineColor: '#85a9ff'
  } : {
    backgroundColor: '#FFFFFF',
    textColor: '#333333',
    gridLineColor: '#e6e6e6',
    lineColor: '#4a90e2'
  }
})

// baseOptions를 함수로 변경하여 테마 색상을 동적으로 적용
const getBaseOptions = (): Record<string, unknown> => {
  const colors = chartColors.value

  return {
    time: {
      useUTC: false,
    },
    title: {
      text: null
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false
        }
      },
      area: {
        marker: {
          enabled: false
        }
      },
      series: {
        fillOpacity: 0.4,
        pointStart: 0,
      }
    },
    chartOption: {
      lineColor: '#FF0000',
      nodatastr: 'No data',
    },
    chart: {
      type: "line",
      height: 140,
      zoomType: 'x',
      backgroundColor: colors.backgroundColor,
    },
    xAxis: {
      gridLineWidth: 0,
      title: {
        enabled: false,
      },
      type: "datetime",
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%m-%d',
        week: '%m-%d',
        month: '%m-%d',
        year: '%Y'
      },
      labels: {
        style: {
          color: colors.textColor,
          fontsize: '11px',
        }
      },
    },
    yAxis: {
      gridLineWidth: 0,
      title: {
        text: null
      },
      labels: {
        style: {
          color: colors.textColor,
          fontsize: '11px',
        }
      }
    },
    tooltip: {
    },
    series: [],
    credits: {
      enabled: false
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '12px',
        color: colors.textColor
      }
    },
    lang: {
      noData: '',
      loading: '불러오는 중'
    },
    data: {
      firstRowAsNames: true,
    }
  }
}

onMounted(async () => {
    isLoading.value = true;
    buildOptions();
    // 차트가 초기화되면 로딩 종료
    setTimeout(() => {
      isLoading.value = false;
    }, 500);
});

// setsensor의 chartdata 변경을 감지하고 자동으로 차트 업데이트
watch(
  () => props.setsensor?.chartdata,
  () => {
    if (chartObj.value) {
      // console.log('MonitorLineChart: chartdata 변경 감지, 차트 업데이트',  props.setsensor.sname);

      isLoading.value = true;

      // 기존 series와 라벨 모두 제거
      while (chartObj.value.series.length > 0) {
        chartObj.value.series[0].remove(false);
      }

      // 기존 label 제거
      // if (chartObj.value.renderer && chartObj.value.renderer.box) {
      //   const labels = chartObj.value.renderer.box.querySelectorAll('.highcharts-label');
      //   labels.forEach((label: any) => {
      //     if (label && label.destroy) {
      //       label.destroy();
      //     }
      //   });
      // }

      // 새로운 series 추가 (데이터가 없어도 처리)
      setChart();

      // 차트 업데이트 완료 후 로딩 종료
      setTimeout(() => {
        isLoading.value = false;
      }, 300);
    }
  },
  { deep: true, immediate: false }
);


// 테마 변경을 감지하고 차트 색상 업데이트
watch(
  () => themeStore.theme,
  () => {
    if (chartObj.value) {
      const colors = chartColors.value

      // 차트 배경색 업데이트
      chartObj.value.update({
        chart: {
          backgroundColor: colors.backgroundColor
        },
        xAxis: {
          labels: {
            style: {
              color: colors.textColor
            }
          }
        },
        yAxis: {
          labels: {
            style: {
              color: colors.textColor
            }
          }
        }
      }, true);
    }
  }
);

function buildOptions() {
  if (!chartRef.value) return

  const options: Record<string, unknown> = getBaseOptions();
  if(options.chart && typeof options.chart === 'object')
    (options.chart as Record<string, unknown>).renderTo = chartid;

  chartObj.value = Highcharts.chart(options);
}

function setChart() {
  if (!chartRef.value) return;
  if(!chartObj.value) return;

  // 테마에 따른 차트 라인 색상
  const chartcolor = chartColors.value.lineColor
  const sensor = props.setsensor;
  const s1: Highcharts.SeriesOptionsType = {
        type: "line",
        name: sensor.sname,
        data: sensor.chartdata as [],
        color: chartcolor,
        showInLegend: false,
        pointInterval : 1000 * 60 * 60, // 1시간 간격
    };

  if(sensor.chartdata && sensor.chartdata.length > 0) {
	  chartObj.value.addSeries(s1);
    addAlertSeries();
  } else {
    // No data 처리
    const ch = chartObj.value as Highcharts.Chart;

		let textX = ch.plotLeft + (ch.plotWidth * 0.5);
		let textY = ch.plotTop + (ch.plotHeight * 0.4);
		const textWidth = 500;
		textX = textX - (textWidth / 2);
    const temp = '데이터 없음';

		ch.renderer
      .label('<div style="width: ' + textWidth + 'px; text-align: center"><span>' + temp + '</span></div>', textX, textY, undefined, undefined, undefined, true)
			.css({
				fontSize: '14px',
        fontWeight: 'normal',
        color: chartColors.value.textColor
			}).add();
  }

  chartObj.value.update({
    tooltip: {
      enabled: true,
      useHTML: true,
      shared: true,
      // function 키워드 사용 — this 바인딩 필요
      formatter: function(this:any) {
        // shared:true 인 경우 this.points 배열이 존재함
        const points = (this as any).points ?? []; // 타입 안전을 위해 as any 사용 가능
        const date = `<span style="font-size:10px">${moment((this as any).x).format('YYYY-MM-DD HH:mm:ss')}</span><br/>`;
        let msg = date;

        for (let i = 0; i < points.length; i++) {
          const p = points[i];
          const color = p?.point?.color ?? '#000';
          const name = p?.series?.name ?? '';
          const value = p?.y ?? '';
          msg += `<span style="color:${color}">● </span>${name}: <strong>${value}</strong><br/>`;
        }

        return msg;
      }
    }
  }, true); // true: redraw 즉시
}

function addAlertSeries() {
  const deviceobj = props.setdevice;
  const sensorobj = props.setsensor;
  const ch = chartObj.value as Highcharts.Chart;

  let alertconfigstr : string = deviceobj.alertconfig ?? null;
    if(alertconfigstr) {
        alertconfigstr = alertconfigstr.replace(/[\r\n]+/g, '\\n');
        const svid = 'sv' + sensorobj.sindex;

        const getvalue = getDeivceAlertValue(svid);
        if(getvalue) {
            const sdata = sensorobj.chartdata
            const limitdata = sdata.map(item => [item[0], getvalue]);
            if(limitdata) {
                ch.addSeries({
                    name: '알림 설정 값',
                    data: limitdata, // 선에 대한 데이터
                    type: 'line', // 선 타입
                    color: 'rgba(255, 165, 0, 0.9)',
                    dashStyle: 'Dot', // 선 스타일 (Solid, Dash, Dot 등)
                    showInLegend: false,
                    pointInterval : 1000 * 60 * 60, // 1시간 간격
                    lineWidth: 2 // 선의 굵기 (기본값은 2)
                });
            }
        }
    }
}


function getDeivceAlertValue(svid:string) {
  const deviceobj = props.setdevice;
	const configstr = deviceobj.config ?? {};
	const config = JSON.parse(configstr);

	let retvalue;
	switch(svid) {
		case 'sv18' :
		case 'sv19' : {
      if(!config.set_sensor[props.setsensor.dfkey].alert)
        return null;

      const guageValue = config.set_sensor[props.setsensor.dfkey].guage || config?.set_gauge || 0;
			retvalue = guageValue;
    }
			break;
		case 'sv5' :{
			// retvalue = Number(config.set_temp);
      if(!config.set_sensor?.[props.setsensor.dfkey].alert)
        return null;

      const guageValue = config.set_sensor[props.setsensor.dfkey].guage || config?.set_gauge || 0;
			retvalue = guageValue;
    }
			break;
		case 'sv6' :
      {
			// retvalue = Number(config.set_humi);
      if(!config.set_sensor[props.setsensor.dfkey].alert)
        return null;

      const guageValue = config.set_sensor[props.setsensor.dfkey].guage || config?.set_gauge || 0;
			retvalue = guageValue;
      }
			break;
	}

	return retvalue;
}


// const loadDeviceByChartData = () => {
//   const nowtime = (new Date()).getTime(); // 현재 시간의 Unix 타임
//   const before30days = nowtime - (30 * 24 * 60 * 60 * 1000);  // 30일 전 (ms)

//   // 입력값을 변수에 담고 문자열 형태로 변환
//   const data = JSON.stringify({
//     'operator': "getSearchDeivceByLineChartEx",
//     'deviceid': props.setdevice.deviceid,
//     'from': before30days,
//     'to': nowtime
//   });

//   // content-type을 설정하고 데이터 송신
//   const xhr = new XMLHttpRequest();
//   xhr.open('POST', '/ajax/ajaxcall');
//   xhr.setRequestHeader('Content-type', "application/json");
//   xhr.send(data);

//   // 데이터 수신이 완료되면 표시
//   xhr.addEventListener('load', function () {
//     var result = xhr.responseText;
//     if (!result || result == '')
//       return;

//     const chartdata = JSON.parse(result);
    
    
//   });
// }

</script>

<style scoped>
.chart-wrapper {
  position: relative;
  min-height: 140px;
}

.chart-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
}

.chart-loading-dark {
  background-color: rgba(18, 30, 45, 0.9) !important;
}

.chart-hidden {
  opacity: 0.3;
}

.highchart-container {
  transition: opacity 0.3s ease;
}
</style>
